import { useContext, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../contexts/AuthContext';
import { supportsMarketData } from '../utils/brokerConnection';
import { resolvePerformanceRangeParams } from '../utils/dateRanges';

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const SYNC_THROTTLE_MS = 30_000;

async function fetchEquitySnapshot(params, token) {
  const url = new URL(`${getBackendUrl()}/performance/equity`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
  });

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const err = new Error(`Performance equity failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

async function syncTodaySnapshot({ accountId, paperTrading, balance, token }) {
  const url = new URL(`${getBackendUrl()}/account_snapshots/sync-today`);
  url.searchParams.set('accountId', accountId);
  url.searchParams.set('paperTrading', String(!!paperTrading));

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(balance ? { balance } : {}),
  });

  if (!res.ok) {
    const err = new Error(`Sync today snapshot failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

const EMPTY_EQUITY = {
  equityCurve: [],
  drawdownCurve: [],
  rangeStart: null,
  rangeEnd: null,
  accountPnlPct: null,
  benchmarkPnlPct: null,
  benchmark: 'SPY',
  benchmarkAvailable: false,
};

/**
 * Daily account snapshot equity curve for charts (Account Overview, etc.).
 * When `balance` is available, upserts today's snapshot then refetches the equity series.
 */
export function useAccountEquitySnapshot({
  timeframe = 'month',
  dateRange = null,
  accountId,
  account = null,
  paperTrading,
  benchmark = 'SPY',
  enabled = true,
  balance = null,
  balancesReady = false,
} = {}) {
  const { user } = useContext(AuthContext);
  const token = user?.token;
  const queryClient = useQueryClient();
  const queryEnabled = !!token && enabled && !!accountId;
  // Brokers without an effective MD source cannot resolve SPY/QQQ via Direct TS.
  const canRequestBenchmark = supportsMarketData(user, account);
  const effectiveBenchmark =
    canRequestBenchmark && benchmark ? benchmark : null;

  const isCustomRange = !!(dateRange?.since && dateRange?.until);
  const rangeParams = resolvePerformanceRangeParams(timeframe, dateRange);
  const params = { ...rangeParams };
  if (effectiveBenchmark) params.benchmark = effectiveBenchmark;
  if (accountId) params.accountId = accountId;
  if (paperTrading !== undefined) params.paperTrading = paperTrading;

  const rangeKeyKind = rangeParams.since ? (isCustomRange ? 'custom' : 'ytd') : timeframe;
  const equityQueryKey = [
    'performance',
    'equity',
    rangeKeyKind,
    rangeParams.since || null,
    rangeParams.until || null,
    accountId,
    paperTrading,
    effectiveBenchmark,
  ];

  const equityQuery = useQuery({
    queryKey: equityQueryKey,
    queryFn: () => fetchEquitySnapshot(params, token),
    enabled: queryEnabled,
    staleTime: 30000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
    retry: 1,
  });

  const lastSyncAtRef = useRef(0);
  const syncInFlightRef = useRef(false);

  useEffect(() => {
    lastSyncAtRef.current = 0;
  }, [accountId, paperTrading]);

  useEffect(() => {
    if (!queryEnabled || !balancesReady || !balance) return;
    if (String(balance.AccountID) !== String(accountId)) return;

    const equity = parseFloat(balance.Equity);
    if (!Number.isFinite(equity)) return;

    const now = Date.now();
    if (syncInFlightRef.current || now - lastSyncAtRef.current < SYNC_THROTTLE_MS) {
      return;
    }

    let cancelled = false;
    syncInFlightRef.current = true;

    (async () => {
      try {
        await syncTodaySnapshot({ accountId, paperTrading, balance, token });
        if (!cancelled) {
          lastSyncAtRef.current = Date.now();
          await queryClient.invalidateQueries({ queryKey: ['performance', 'equity'] });
        }
      } catch {
        // Equity chart still works from prior snapshots; sync retries on next balance refresh.
      } finally {
        syncInFlightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    queryEnabled,
    balancesReady,
    accountId,
    paperTrading,
    token,
    balance?.AccountID,
    balance?.Equity,
    balance?.TodaysProfitLoss,
    queryClient,
  ]);

  return {
    equity: equityQuery.data || {
      ...EMPTY_EQUITY,
      benchmark: effectiveBenchmark || benchmark || 'SPY',
    },
    isLoading: equityQuery.isLoading,
    isError: equityQuery.isError,
  };
}
